import {
  formatDateToDDMMYYYY,
  formatDateToddmmyyhhmm,
} from '../../../utils/dateformatter';
import { PricingPeriodData } from '../../../constants/masterData';
import {
  getListData,
  mapDeliveryDetailRow,
  isAlphaFilled,
} from './deliveryDetailUtils';
import { LPG_PRODUCT_ID } from '../../../shared/offer-domain/constants';
import { pick } from '../../../shared/offer-domain/pick';
import {
  CARGO_DELIVERY_COLUMNS,
  mapDeliveryToRows,
} from '../../FreightCost/component/CargoDeliveryTable';

export const PROCUREMENT_TYPE_MAP = {
  '': '-',
  PelelanganKhusus: 'Pelelangan Khusus',
  PemilihanLangsung: 'Pemilihan Langsung',
  TunjukLangsung: 'Tunjuk Langsung',
};

export const displayValue = (value, fallback = '-') => {
  if (value === null || value === undefined || value === '') return fallback;
  return value;
};

export const getTenderEvaluationTabCompletion = (
  procurement,
  completionKey
) => {
  if (!procurement || !completionKey) return null;
  return procurement[completionKey] ?? null;
};

export const getPaymentCurrencyLabel = paymentCurrency => {
  if (!paymentCurrency) return '-';
  const name = paymentCurrency.currencyName ?? paymentCurrency.name;
  const code = paymentCurrency.currencyCode;
  if (name && code) return `${name} (${code})`;
  return name || code || '-';
};

export const getContractTypeLabel = contractType => {
  const value = parseInt(contractType, 10);
  if (value === 1) return 'TERM';
  if (value === 2) return 'SPOT';
  return '-';
};

export const formatPeriodMonth = isoDate => {
  if (!isoDate) return '-';
  const date = new Date(isoDate);
  if (Number.isNaN(date.getTime())) return '-';
  return new Intl.DateTimeFormat('en-GB', {
    month: 'short',
    year: 'numeric',
  }).format(date);
};

export const formatPeriodRange = procurement => {
  if (!procurement) return '-';
  const contractType = parseInt(procurement.contractType, 10);
  if (contractType === 2) {
    return formatPeriodMonth(
      procurement.contractPeriodSpot || procurement.contractPeriodTermStart
    );
  }
  if (contractType === 1) {
    const start = formatPeriodMonth(procurement.contractPeriodTermStart);
    const end = formatPeriodMonth(procurement.contractPeriodTermEnd);
    if (start === '-' && end === '-') return '-';
    return `${start} - ${end}`;
  }
  return '-';
};

export const isSpotContract = procurement =>
  parseInt(procurement?.contractType, 10) === 2;

export const getContractPeriodProps = procurement => {
  if (!procurement) {
    return {
      isSpot: false,
      startLabel: 'Start Period',
      startValue: '-',
      endLabel: 'End Period',
      endValue: '-',
    };
  }

  if (isSpotContract(procurement)) {
    return {
      isSpot: true,
      startLabel: 'Period',
      startValue: formatPeriodMonth(
        procurement.contractPeriodSpot || procurement.contractPeriodTermStart
      ),
      endLabel: null,
      endValue: null,
    };
  }

  return {
    isSpot: false,
    startLabel: 'Start Period',
    startValue: formatPeriodMonth(procurement.contractPeriodTermStart),
    endLabel: 'End Period',
    endValue: formatPeriodMonth(procurement.contractPeriodTermEnd),
  };
};

export const formatProposalDeadline = isoDate =>
  isoDate ? formatDateToddmmyyhhmm(isoDate) : '-';

export const formatPricingPeriodRange = (start, end) => {
  const startLabel = start ? formatDateToDDMMYYYY(start) : '';
  const endLabel = end ? formatDateToDDMMYYYY(end) : '';
  if (!startLabel && !endLabel) return '-';
  if (startLabel && endLabel) return `${startLabel} - ${endLabel}`;
  return startLabel || endLabel;
};

export const getPricingPeriodLabel = pricingPeriod => {
  const value = parseInt(pricingPeriod, 10);
  const option = PricingPeriodData[0]?.options?.find(
    item => item.value === value
  );
  if (option?.label) return option.label;
  if (
    pricingPeriod === null ||
    pricingPeriod === undefined ||
    pricingPeriod === ''
  ) {
    return '-';
  }
  return String(pricingPeriod);
};

export const getPricingPeriodDetailLabel = pricingPeriod => {
  const value = parseInt(pricingPeriod, 10);
  if (value === 1) return 'Publish Index Period';
  if (value === 2) return 'Schema B/L Date';
  if (value === 3) return 'Fixed Price';
  return 'Pricing Detail';
};

export const getPricingPeriodDetailValue = data => {
  if (!data) return '-';
  const value = parseInt(data.pricingPeriod, 10);
  if (value === 1) {
    return formatPricingPeriodRange(
      data.startDatePublishIndex,
      data.endDatePublishIndex
    );
  }
  if (value === 2) return data.schemaBL || '-';
  if (value === 3) {
    if (data.priceAmount === null || data.priceAmount === undefined) return '-';
    return data.priceAmount;
  }
  return '-';
};

export const getProcurementTypeLabel = type =>
  PROCUREMENT_TYPE_MAP[type] || type || '-';

export const getTenderEvaluationBadgeColor = status => {
  const lower = (status || '').toString().toLowerCase();
  if (lower === 'ready for evaluation') return 'primary';
  if (lower === 'under evaluation') return 'success';
  if (lower === 'awaiting evaluation') return 'danger';
  return 'primary';
};

export const getTenderEvaluationStatusConfig = statusWording => {
  const color = getTenderEvaluationBadgeColor(statusWording);
  const colorMap = {
    success: 'bg-success text-white',
    danger: 'bg-danger text-white',
    primary: 'bg-primary text-white',
  };
  return {
    label: statusWording || '-',
    className: colorMap[color] || 'bg-secondary text-white',
  };
};

export const isTenderEvaluationNotClosed = procurement =>
  procurement?.evaluationStatus == null ||
  procurement?.evaluationStatusWording === 'Awaiting Evaluation';

export const getEvaluationOperationalLockReason = procurement => {
  const statusWording = (procurement?.evaluationStatusWording || '')
    .toString()
    .toLowerCase()
    .trim();
  const statusRaw = procurement?.evaluationStatus;
  const statusKey = String(statusRaw ?? '')
    .toLowerCase()
    .trim();

  const isUnderEvaluation =
    statusWording === 'under evaluation' ||
    statusKey === 'underevaluation' ||
    Number(statusRaw) === 3;

  if (isUnderEvaluation) return null;

  const isReady =
    statusWording === 'ready for evaluation' ||
    statusKey === 'requested' ||
    Number(statusRaw) === 2;

  if (isReady) return 'not-started';

  const isAwaiting =
    !statusWording ||
    statusWording === '-' ||
    statusWording === 'awaiting evaluation' ||
    statusWording === 'await evaluation' ||
    statusKey === 'submitted' ||
    statusRaw == null ||
    statusRaw === '' ||
    Number(statusRaw) === 1;

  if (isAwaiting) return 'awaiting';

  return 'not-started';
};

export const canAccessEvaluationOperationalData = procurement =>
  getEvaluationOperationalLockReason(procurement) === null;

export const isFreightCostAvailable = procurement => {
  const status = procurement?.freightCostStatus;
  if (status === null || status === undefined || status === '') return false;

  if (typeof status === 'string') {
    const label = status.trim().toLowerCase();
    if (label === 'not available' || label === 'notavailable') return false;
    if (
      label === 'available' ||
      label === 'released' ||
      label === 'release' ||
      label === 'true'
    ) {
      return true;
    }
  }

  // Mirror FreightCostForm: freightCostStatus == 0 ? false : true
  return status != 0 && status !== false;
};

/** Mirror Hps module (`statusHPS2` / getProcurementStatusConfig): 1|true = Available. */
export const isHpsAvailable = procurement => {
  const status =
    procurement?.statusHPS2 ??
    procurement?.StatusHPS2 ??
    procurement?.statusHps ??
    procurement?.StatusHps;
  if (status === null || status === undefined || status === '') return false;

  if (typeof status === 'boolean') return status === true;

  if (typeof status === 'string') {
    const label = status.trim().toLowerCase();
    if (
      label === 'available' ||
      label === 'true' ||
      label === '1' ||
      label === 'released' ||
      label === 'release'
    ) {
      return true;
    }
    if (
      label === 'not available' ||
      label === 'notavailable' ||
      label === 'false' ||
      label === '0'
    ) {
      return false;
    }
  }

  return Number(status) === 1;
};

export const shouldShowFreightTab = procurement =>
  Boolean(procurement?.isReqFreightAvailabilityEnable);

export const unwrapTenderEvaluationList = result => {
  const payload = result?.data ?? result ?? {};
  const items = payload.items ?? payload.Items ?? [];
  const totalItems = payload.totalItems ?? payload.TotalItems ?? 0;

  return {
    items: items.map(mapProcurementToTenderEvaluationRow).filter(Boolean),
    totalItems,
  };
};

export const normalizeSubmittedBidder = raw => {
  if (!raw || typeof raw !== 'object') return null;

  return {
    id: pick(raw, 'id', 'Id'),
    procurementId: pick(raw, 'procurementId', 'ProcurementId'),
    companyMitraId: pick(raw, 'companyMitraId', 'CompanyMitraId'),
    idOffer: pick(raw, 'idOffer', 'IdOffer'),
    companyIdentityMask: pick(
      raw,
      'companyIdentityMask',
      'CompanyIdentityMask'
    ),
    statusInvitation: pick(raw, 'statusInvitation', 'StatusInvitation'),
    statusOffering: pick(raw, 'statusOffering', 'StatusOffering'),
    submissionStatus: pick(raw, 'submissionStatus', 'SubmissionStatus'),
    submissionDate: pick(raw, 'submissionDate', 'SubmissionDate'),
    submissionBy: pick(raw, 'submissionBy', 'SubmissionBy'),
    isLateSubmission: pick(raw, 'isLateSubmission', 'IsLateSubmission'),
    isRead: pick(raw, 'isRead', 'IsRead'),
    unreadResponseCount:
      Number(pick(raw, 'unreadResponseCount', 'UnreadResponseCount')) || 0,
    statusClarification:
      pick(raw, 'statusClarification', 'StatusClarification') ??
      pick(raw, 'clarificationStatus', 'ClarificationStatus'),
    statusClarificationLabel: pick(
      raw,
      'statusClarificationLabel',
      'StatusClarificationLabel'
    ),
    statusClarificationProgress: pick(
      raw,
      'statusClarificationProgress',
      'StatusClarificationProgress'
    ),
    statusClarificationProgressLabel: pick(
      raw,
      'statusClarificationProgressLabel',
      'StatusClarificationProgressLabel'
    ),
    quantityDeliveryTerms:
      raw?.quantityDeliveryTerms ?? raw?.QuantityDeliveryTerms ?? [],
    respondClauseDetails:
      raw?.respondClauseDetails ?? raw?.RespondClauseDetails ?? [],
  };
};

export const unwrapSubmittedBidderList = result => {
  if (!result) return { items: [], totalItems: 0 };

  const nested = result.data ?? result.Data;
  const payload = nested && (nested.items || nested.Items) ? nested : result;

  const items = payload.items ?? payload.Items ?? [];
  const totalItems =
    payload.totalItems ?? payload.TotalItems ?? items.length ?? 0;

  return {
    items: items.map(normalizeSubmittedBidder).filter(Boolean),
    totalItems,
  };
};

export const mapSubmittedBidderToDeliveryRow = bidder => ({
  procurementMitraId: bidder.id,
  idOffer: bidder.idOffer,
  companyIdentityMask: bidder.companyIdentityMask,
  submissionStatus: bidder.submissionStatus,
  isCapable: Number(bidder.submissionStatus) === 2,
});

export const buildRequirementsFromParcel = parcel => {
  const rows = parcel?.incotermRows ?? [];

  if (!rows.length) {
    return [
      {
        deliveryBasis: null,
        incoterm: null,
        route: null,
        requirementIndex: 0,
        timeline: null,
        quantityIncotermId: null,
      },
    ];
  }

  return rows.map((row, index) => ({
    ...row,
    timeline: parcel?.incotermTimelineItems?.[index] ?? null,
    requirementIndex: index,
    quantityIncotermId:
      row.quantityIncotermId ??
      row.QuantityIncotermId ??
      pick(row, 'quantityIncotermId', 'QuantityIncotermId'),
  }));
};

export const getRequirementIncotermRouteLabel = requirement => {
  const label = [requirement?.incoterm, requirement?.route]
    .filter(Boolean)
    .join(' - ');
  return label || '-';
};

const resolveMatchValue = value => {
  if (value === null || value === undefined) return '';
  if (typeof value === 'object') {
    return String(value.name ?? value.Name ?? '').trim();
  }
  return String(value).trim();
};

const normalizeMatchKey = value => {
  const text = resolveMatchValue(value).toLowerCase();
  return text === '-' ? '' : text;
};

export const isRowCapable = (row, useAlphaField = false) => {
  const capable =
    row?.isCapable ??
    row?.capable ??
    row?.IsCapable ??
    row?.Capable ??
    row?.isIsCapable ??
    row?.IsIsCapable;

  if (
    capable === true ||
    capable === 'true' ||
    capable === 1 ||
    capable === '1'
  ) {
    return true;
  }
  if (
    capable === false ||
    capable === 'false' ||
    capable === 0 ||
    capable === '0'
  ) {
    return false;
  }

  if (useAlphaField) return isAlphaFilled(row?.alpha);

  return (
    isAlphaFilled(row?.dyed ?? row?.unitBbl) ||
    isAlphaFilled(row?.undyed ?? row?.cbm)
  );
};

export const unwrapDeliveryDetailGroups = result => {
  const rawData =
    result?.data?.data ||
    result?.data?.Data ||
    result?.data?.items ||
    result?.data?.Items ||
    result?.data ||
    [];

  if (Array.isArray(rawData)) return rawData;
  return rawData ? [rawData] : [];
};

export const resolveDeliveryDetailParentId = parent =>
  pick(parent, 'procurementMitraId', 'ProcurementMitraId') ??
  pick(parent, 'id', 'Id');

export const deliveryDetailRowMatchesBidder = (row, bidder) => {
  if (!row || !bidder?.id) return false;

  const bidderId = String(bidder.id);
  const candidateIds = [row.parentId, row.procurementMitraId, row.id]
    .filter(Boolean)
    .map(String);

  return candidateIds.includes(bidderId);
};

export const getIncotermRouteKey = (incoterm, route) =>
  `${normalizeMatchKey(incoterm)}|${normalizeMatchKey(route)}`;

export const offerRowMatchesRequirement = (row, requirement, parcelId) => {
  const rowParcelId = pick(
    row,
    'quantityDeliveryTermId',
    'QuantityDeliveryTermId'
  );

  if (rowParcelId && parcelId && String(rowParcelId) !== String(parcelId)) {
    return false;
  }

  const requirementIncotermId =
    requirement?.quantityIncotermId ??
    pick(requirement, 'quantityIncotermId', 'QuantityIncotermId');
  const rowIncotermId = pick(row, 'quantityIncotermId', 'QuantityIncotermId');

  if (requirementIncotermId && rowIncotermId) {
    if (String(requirementIncotermId) !== String(rowIncotermId)) {
      return false;
    }
    const requirementLocationId =
      requirement?.incotermLocationId ??
      pick(requirement, 'incotermLocationId', 'IncotermLocationId');
    const rowLocationId = pick(row, 'incotermLocationId', 'IncotermLocationId');
    if (requirementLocationId) {
      return String(rowLocationId ?? '') === String(requirementLocationId);
    }
    return true;
  }

  const requirementKey = getIncotermRouteKey(
    requirement?.incoterm,
    requirement?.route
  );
  const rowKey = getIncotermRouteKey(row.incoterm, row.route);

  if (requirementKey !== '|' && rowKey !== '|') {
    return requirementKey === rowKey;
  }

  return true;
};

export const groupViewDetailBidderByParcels = (rows = [], parcels = []) => {
  const grouped = {};

  parcels.forEach(parcel => {
    const parcelId = parcel?.id;
    if (!parcelId) return;

    const requirements = buildRequirementsFromParcel(parcel);
    const byRequirement = {};

    requirements.forEach((requirement, requirementIndex) => {
      byRequirement[requirementIndex] = rows.filter(row =>
        offerRowMatchesRequirement(row, requirement, parcelId)
      );
    });

    grouped[parcelId] = byRequirement;
  });

  return grouped;
};

const mapViewDetailBidderItem = (raw, bidderMap = {}) => {
  if (!raw || typeof raw !== 'object') return null;

  const procurementMitraId =
    pick(raw, 'procurementMitraId', 'ProcurementMitraId') ??
    pick(raw, 'id', 'Id');
  const bidder = bidderMap[procurementMitraId] ?? {};

  return {
    ...mapDeliveryDetailRow({ id: procurementMitraId }, raw),
    procurementMitraId,
    idOffer:
      pick(raw, 'idOffer', 'IdOffer') ??
      bidder.idOffer ??
      pick(raw, 'idOffer', 'IdOffer'),
    companyIdentityMask:
      pick(raw, 'companyIdentityMask', 'CompanyIdentityMask') ??
      bidder.companyIdentityMask,
    quantityDeliveryTermId: pick(
      raw,
      'quantityDeliveryTermId',
      'QuantityDeliveryTermId'
    ),
    quantityIncotermId: pick(raw, 'quantityIncotermId', 'QuantityIncotermId'),
    requirementIndex: pick(raw, 'requirementIndex', 'RequirementIndex'),
    isCapable:
      pick(raw, 'isCapable', 'IsCapable') ??
      pick(raw, 'capable', 'Capable') ??
      pick(raw, 'isIsCapable', 'IsIsCapable'),
  };
};

export const unwrapViewDetailBidderList = (result, bidderMap = {}) => {
  if (!result) return [];

  const root = result.data ?? result.Data ?? result;
  const nested = root?.data ?? root?.Data;
  const payload =
    nested && (nested.items || nested.Items || Array.isArray(nested))
      ? nested
      : root;

  const flatItems = payload?.items ?? payload?.Items;
  if (Array.isArray(flatItems)) {
    if (flatItems[0]?.listData || flatItems[0]?.ListData) {
      return flatItems
        .flatMap(parent => {
          const parentId =
            pick(parent, 'procurementMitraId', 'ProcurementMitraId') ??
            pick(parent, 'id', 'Id');
          const bidder = bidderMap[parentId] ?? {};

          return getListData(parent).map(details =>
            mapViewDetailBidderItem(
              {
                ...details,
                procurementMitraId: parentId,
                idOffer:
                  pick(parent, 'idOffer', 'IdOffer') ?? bidder.idOffer ?? null,
                companyIdentityMask:
                  pick(parent, 'companyIdentityMask', 'CompanyIdentityMask') ??
                  bidder.companyIdentityMask ??
                  null,
              },
              bidderMap
            )
          );
        })
        .filter(Boolean);
    }

    return flatItems
      .map(item => mapViewDetailBidderItem(item, bidderMap))
      .filter(Boolean);
  }

  if (Array.isArray(payload)) {
    if (payload[0]?.listData || payload[0]?.ListData) {
      return payload
        .flatMap(parent => {
          const parentId =
            pick(parent, 'procurementMitraId', 'ProcurementMitraId') ??
            pick(parent, 'id', 'Id');
          const bidder = bidderMap[parentId] ?? {};

          return getListData(parent).map(details =>
            mapViewDetailBidderItem(
              {
                ...details,
                procurementMitraId: parentId,
                idOffer:
                  pick(parent, 'idOffer', 'IdOffer') ?? bidder.idOffer ?? null,
                companyIdentityMask:
                  pick(parent, 'companyIdentityMask', 'CompanyIdentityMask') ??
                  bidder.companyIdentityMask ??
                  null,
              },
              bidderMap
            )
          );
        })
        .filter(Boolean);
    }

    return payload
      .map(item => mapViewDetailBidderItem(item, bidderMap))
      .filter(Boolean);
  }

  return [];
};

export const getSubmittedBidderReadStatusConfig = bidder => {
  if (bidder?.isRead === true) {
    return { label: 'Read', color: 'success' };
  }
  if (bidder?.isRead === false) {
    return { label: 'Unread', color: 'info' };
  }
  return { label: 'Submitted', color: 'primary' };
};

export const getEvaluationTypeProduct = procurement => {
  const productId =
    procurement?.productType?.product?.id ??
    procurement?.productType?.Product?.Id;
  return productId === LPG_PRODUCT_ID ? 1 : 2;
};

export const buildBidderLookupMap = bidders => {
  const map = {};
  (bidders ?? []).forEach(bidder => {
    const key = bidder?.procurementMitraId ?? bidder?.id;
    if (key) map[key] = bidder;
  });
  return map;
};

export const buildOfferFormViewState = procurement => ({
  id: procurement?.id ?? null,
  productName: procurement?.productType?.product?.name ?? '',
  productTypename: procurement?.productType?.description ?? '',
  productNameId: procurement?.productType?.product?.id ?? '',
  productTypeId: procurement?.productType?.id ?? null,
  contractPeriodTermStart: procurement?.contractPeriodTermStart ?? null,
  contractPeriodTermEnd: procurement?.contractPeriodTermEnd ?? null,
  proposalSubmissionDeadline: procurement?.proposalSubmissionDeadline ?? null,
  proposalValidityPeriodMax: procurement?.proposalValidityPeriodMax ?? null,
  operationalTolerance: procurement?.operationalTolerance ?? null,
  procurementClauseDetails: procurement?.procurementClauseDetails ?? [],
});

export const buildOfferFormMainViewState = bidder => ({
  id: bidder?.id ?? null,
  ids: bidder?.procurementId ?? null,
  idOffer: bidder?.idOffer ?? null,
  companyName: bidder?.companyIdentityMask ?? '-',
  statusOfferingWordingFlag: true,
});

export const buildQtyDeliveryItemsFromBidder = bidder => ({
  items: (bidder?.quantityDeliveryTerms ?? bidder?.QuantityDeliveryTerms ?? [])
    .map(qdt => ({
      id: pick(qdt, 'id', 'Id'),
      deliveryUnit: pick(qdt, 'deliveryUnit', 'DeliveryUnit'),
      procurementId:
        pick(qdt?.procurement, 'id', 'Id') ?? bidder?.procurementId ?? null,
    }))
    .filter(item => item.id),
});

export const buildDeliveryItemsByTermIdFromBidder = bidder => {
  const map = {};
  (
    bidder?.quantityDeliveryTerms ??
    bidder?.QuantityDeliveryTerms ??
    []
  ).forEach(qdt => {
    const termId = pick(qdt, 'id', 'Id');
    if (!termId) return;
    map[termId] = getListData(qdt).map(details =>
      mapDeliveryDetailRow(bidder, details)
    );
  });
  return map;
};

export const flattenSubmittedBidderDeliveryDetails = (bidders = []) =>
  (bidders ?? []).flatMap(bidder => {
    const quantityDeliveryTerms =
      bidder?.quantityDeliveryTerms ?? bidder?.QuantityDeliveryTerms ?? [];

    return quantityDeliveryTerms.flatMap(qdt => {
      const quantityDeliveryTermId = pick(qdt, 'id', 'Id');
      const listData = getListData(qdt);

      return listData.map((details, requirementIndex) => ({
        ...mapDeliveryDetailRow(bidder, details),
        procurementMitraId: bidder.id,
        parentId: bidder.id,
        idOffer: bidder.idOffer,
        companyIdentityMask: bidder.companyIdentityMask,
        requirementIndex,
        quantityDeliveryTermId,
        quantityIncotermId: pick(
          details,
          'quantityIncotermId',
          'QuantityIncotermId'
        ),
      }));
    });
  });

export const getOfferRowIdentityKey = (row, keyField = 'rowKey') => {
  if (keyField === 'selectionKey') {
    return (
      row?.selectionKey ??
      `${row?.bidder?.id ?? row?.bidder?.idOffer ?? 'bidder'}-${row?.kind ?? 'offer'}`
    );
  }
  return row?.rowKey ?? null;
};

const isTruthyFlag = value =>
  value === true || value === 1 || value === '1' || value === 'true';

export const isOfferRowImprovement = row => {
  const detailRow = row?.detailRow ?? {};
  const kind = row?.kind ?? 'offer';

  if (kind === 'counter') {
    return isTruthyFlag(
      detailRow.counterIsImprovement ??
        pick(detailRow, 'counterIsImprovement', 'CounterIsImprovement')
    );
  }

  return isTruthyFlag(
    detailRow.isImprovement ?? pick(detailRow, 'isImprovement', 'IsImprovement')
  );
};

export const filterImprovementOfferRows = (rows = []) =>
  rows.filter(isOfferRowImprovement);

export const getOfferRowImprovementStatusLabel = row =>
  isOfferRowImprovement(row) ? 'Requested' : 'Not Requested';

export const buildImprovementSelectionFromOfferRow = ({
  row,
  parcelId,
  requirementIndex,
  requirement,
}) => {
  const bidder = row?.bidder ?? {};
  const detailRow = row?.detailRow ?? {};
  const kind = row?.kind ?? 'offer';
  const quantityIncotermId =
    detailRow.quantityIncotermId ??
    pick(detailRow, 'quantityIncotermId', 'QuantityIncotermId') ??
    requirement?.quantityIncotermId ??
    pick(requirement, 'quantityIncotermId', 'QuantityIncotermId') ??
    null;
  const quantityIncotermCounterId =
    detailRow.quantityIncotermCounterId ??
    pick(detailRow, 'quantityIncotermCounterId', 'QuantityIncotermCounterId') ??
    null;

  return {
    quantityIncotermId,
    quantityIncotermCounterId,
    kind,
    selectionKey: row?.selectionKey ?? row?.rowKey ?? null,
    rowKey: row?.rowKey ?? null,
    procurementMitraId: bidder.id ?? null,
    idOffer: bidder.idOffer ?? null,
    quantityDeliveryTermId:
      parcelId ?? detailRow.quantityDeliveryTermId ?? null,
    requirementIndex,
  };
};

export const collectImprovementSelections = ({
  compliantRows = [],
  selectedKeys = [],
  parcelId,
  requirementIndex,
  requirement,
  keyField = 'rowKey',
}) => {
  const selectedSet = new Set(selectedKeys);
  const seen = new Set();
  const entries = [];

  compliantRows.forEach(row => {
    const key = getOfferRowIdentityKey(row, keyField);
    if (!key || !selectedSet.has(key)) return;
    if (isOfferRowImprovement(row)) return;

    const entry = buildImprovementSelectionFromOfferRow({
      row,
      parcelId,
      requirementIndex,
      requirement,
    });
    const dedupeKey = [
      entry.kind ?? 'offer',
      entry.procurementMitraId ?? '',
      entry.quantityIncotermId ?? '',
      entry.quantityIncotermCounterId ?? '',
    ].join('|');
    if (seen.has(dedupeKey)) return;
    seen.add(dedupeKey);
    entries.push(entry);
  });

  return entries;
};

export const buildRequestImprovementBulkPayloads = ({
  improvements = [],
  message,
  messages,
  closingDateImprovement,
  date,
}) => {
  const offerItems = [];
  const counterItems = [];
  const seenOffer = new Set();
  const seenCounter = new Set();
  const messageImprovement = String(message ?? messages ?? '').trim() || null;
  const closingDateValue = closingDateImprovement ?? date ?? null;

  improvements.forEach(entry => {
    if (entry?.kind === 'counter') {
      const quantityIncotermCounterId = String(
        entry?.quantityIncotermCounterId ??
          entry?.QuantityIncotermCounterId ??
          ''
      ).trim();
      if (!quantityIncotermCounterId) return;
      const key = `${quantityIncotermCounterId}`;
      if (seenCounter.has(key)) return;
      seenCounter.add(key);
      counterItems.push(quantityIncotermCounterId);
      return;
    }

    const quantityIncotermId = String(
      entry?.quantityIncotermId ?? entry?.QuantityIncotermId ?? ''
    ).trim();
    if (!quantityIncotermId) return;
    const key = `${quantityIncotermId}`;
    if (seenOffer.has(key)) return;
    seenOffer.add(key);
    offerItems.push(quantityIncotermId);
  });

  const payloads = [];
  if (offerItems.length) {
    payloads.push({
      endpoint: 'bulk-view-detail-bidder',
      body: {
        quantityIncotermIds: offerItems,
        closingDateImprovement: closingDateValue,
        messageImprovement,
      },
    });
  }
  if (counterItems.length) {
    payloads.push({
      endpoint: 'bulk-view-detail-bidder-counter',
      body: {
        quantityIncotermCounterIds: counterItems,
        closingDateImprovement: closingDateValue,
        messageImprovement,
      },
    });
  }

  return payloads;
};

export const flattenEvaluationDeliveryDetail = (
  result,
  bidderMap = {},
  quantityDeliveryTermId = null
) => {
  const groups = unwrapDeliveryDetailGroups(result);

  return groups.flatMap(parent => {
    const parentId = resolveDeliveryDetailParentId(parent);
    const bidder = bidderMap[parentId] ?? {};
    const listData = getListData(parent);

    if (listData.length) {
      return listData.map((details, requirementIndex) => ({
        ...mapDeliveryDetailRow(parent, details),
        procurementMitraId: parentId,
        parentId,
        idOffer:
          bidder.idOffer ??
          pick(parent, 'idOffer', 'IdOffer') ??
          pick(details, 'idOffer', 'IdOffer'),
        companyIdentityMask:
          bidder.companyIdentityMask ??
          pick(parent, 'companyIdentityMask', 'CompanyIdentityMask') ??
          pick(details, 'companyIdentityMask', 'CompanyIdentityMask'),
        requirementIndex,
        quantityDeliveryTermId:
          quantityDeliveryTermId ??
          pick(parent, 'quantityDeliveryTermId', 'QuantityDeliveryTermId') ??
          pick(details, 'quantityDeliveryTermId', 'QuantityDeliveryTermId'),
        quantityIncotermId: pick(
          details,
          'quantityIncotermId',
          'QuantityIncotermId'
        ),
      }));
    }

    const flatParentId =
      pick(parent, 'procurementMitraId', 'ProcurementMitraId') ?? parentId;
    const flatBidder = bidderMap[flatParentId] ?? bidder;

    return [
      {
        ...mapDeliveryDetailRow({ id: flatParentId }, parent),
        procurementMitraId: flatParentId,
        parentId: flatParentId,
        idOffer: flatBidder.idOffer ?? pick(parent, 'idOffer', 'IdOffer'),
        companyIdentityMask:
          flatBidder.companyIdentityMask ??
          pick(parent, 'companyIdentityMask', 'CompanyIdentityMask'),
        requirementIndex:
          pick(parent, 'requirementIndex', 'RequirementIndex') ?? 0,
        quantityDeliveryTermId:
          quantityDeliveryTermId ??
          pick(parent, 'quantityDeliveryTermId', 'QuantityDeliveryTermId'),
        quantityIncotermId: pick(
          parent,
          'quantityIncotermId',
          'QuantityIncotermId'
        ),
      },
    ];
  });
};

const pickFilledPricingValue = (...values) => {
  for (const value of values) {
    if (value === null || value === undefined) continue;
    const text = String(value).trim();
    if (!text || text === '-') continue;
    if (Number(text) === 0) continue;
    return value;
  }
  return '';
};

/** Merge get-delivery-detail pricing (Offer source of truth) onto submitted-bidder rows. */
export const mergeDeliveryDetailPricingIntoRows = (
  rows = [],
  detailRows = []
) => {
  if (!rows.length || !detailRows.length) return rows;

  const byKey = new Map();
  detailRows.forEach(row => {
    const mitra = String(row.procurementMitraId ?? row.parentId ?? '');
    const detailId = String(row.quantityInformationDetailId ?? '');
    const incotermId = String(row.quantityIncotermId ?? '');
    if (!mitra || !detailId) return;
    byKey.set(`${mitra}|${detailId}|${incotermId}`, row);
    const looseKey = `${mitra}|${detailId}|`;
    if (!byKey.has(looseKey)) byKey.set(looseKey, row);
  });

  return rows.map(item => {
    const mitra = String(item.procurementMitraId ?? item.parentId ?? '');
    const detailId = String(item.quantityInformationDetailId ?? '');
    const incotermId = String(item.quantityIncotermId ?? '');
    const match =
      byKey.get(`${mitra}|${detailId}|${incotermId}`) ??
      byKey.get(`${mitra}|${detailId}|`);
    if (!match) return item;

    const counterAlpha = pickFilledPricingValue(
      item.counterAlpha,
      match.counterAlpha,
      item.alpha2,
      match.alpha2,
      item.alphaMitra2,
      match.alphaMitra2
    );
    const counterDyed = pickFilledPricingValue(
      item.counterDyed,
      match.counterDyed,
      item.dyed2,
      match.dyed2
    );
    const counterUndyed = pickFilledPricingValue(
      item.counterUndyed,
      match.counterUndyed,
      item.undyed2,
      match.undyed2
    );

    return {
      ...item,
      alpha: pickFilledPricingValue(item.alpha, match.alpha) || item.alpha,
      alpha2:
        pickFilledPricingValue(item.alpha2, match.alpha2, counterAlpha) ||
        item.alpha2,
      counterAlpha,
      counterDyed,
      counterUndyed,
      dyed2: pickFilledPricingValue(item.dyed2, match.dyed2) || item.dyed2,
      undyed2:
        pickFilledPricingValue(item.undyed2, match.undyed2) || item.undyed2,
      alphaHps:
        pickFilledPricingValue(item.alphaHps, match.alphaHps, item.hpsAlpha) ||
        item.alphaHps,
      alphaDyedHps:
        pickFilledPricingValue(
          item.alphaDyedHps,
          match.alphaDyedHps,
          item.hpsAlphaDyed
        ) || item.alphaDyedHps,
      alphaUndyedHps:
        pickFilledPricingValue(
          item.alphaUndyedHps,
          match.alphaUndyedHps,
          item.hpsAlphaUndyed
        ) || item.alphaUndyedHps,
      matchingHps: item.matchingHps ?? match.matchingHps ?? null,
      quantityIncotermCounterId:
        item.quantityIncotermCounterId ?? match.quantityIncotermCounterId,
      counterIncoterm: item.counterIncoterm ?? match.counterIncoterm,
      counterRoute: item.counterRoute ?? match.counterRoute,
      counterLoadingPort: item.counterLoadingPort ?? match.counterLoadingPort,
      counterDeliveryBasis:
        item.counterDeliveryBasis ?? match.counterDeliveryBasis,
      counterParcelSize: item.counterParcelSize ?? match.counterParcelSize,
    };
  });
};

export const mapProcurementToTenderEvaluationRow = item => {
  if (!item) return null;

  const productTypeRaw = item.productType ?? item.ProductType;
  const productRaw = productTypeRaw?.product ?? productTypeRaw?.Product;

  return {
    id: pick(item, 'id', 'Id'),
    idProc: pick(item, 'procurementNumber', 'ProcurementNumber'),
    title: pick(item, 'titleProcurement', 'TitleProcurement'),
    procType: getProcurementTypeLabel(
      pick(item, 'procurementType', 'ProcurementType')
    ),
    description: pick(item, 'description', 'Description'),
    productType: {
      main: productRaw?.name ?? productRaw?.Name ?? '-',
      sub: productTypeRaw?.description ?? productTypeRaw?.Description ?? '-',
    },
    evaluationStatusWording:
      pick(item, 'evaluationStatusWording', 'EvaluationStatusWording') ?? '-',
    statusHps: pick(item, 'statusHps', 'StatusHps'),
    unreadResponseCount:
      Number(pick(item, 'unreadResponseCount', 'UnreadResponseCount')) || 0,
  };
};

export const ATTENDANCE_CONFIRMATION_COLUMNS = [
  { header: 'No', key: 'no' },
  { header: 'Nama Kehadiran', key: 'namaKehadiran' },
  { header: 'Jabatan', key: 'jabatan' },
  { header: 'Email', key: 'email' },
  { header: 'Status', key: 'status' },
  { header: 'Keterangan', key: 'keterangan' },
];

export const getInvitationEvaluationStatusBadgeConfig = (
  invitationEvaluationStatus,
  invitationEvaluationStatusLabel
) => {
  const isConfirmed = Number(invitationEvaluationStatus) === 1;

  return {
    label:
      invitationEvaluationStatusLabel ||
      (isConfirmed ? 'Confirmed' : 'Unconfirmed'),
    className: `badge fs-12 ${isConfirmed ? 'bg-success' : 'bg-dark'}`,
  };
};

export const normalizeAttendanceConfirmation = raw => {
  if (!raw || typeof raw !== 'object') return null;

  const invitationEvaluationStatus = pick(
    raw,
    'invitationEvaluationStatus',
    'InvitationEvaluationStatus'
  );

  return {
    id: pick(raw, 'id', 'Id', 'userLogin', 'UserLogin'),
    namaKehadiran: pick(raw, 'name', 'Name'),
    jabatan: pick(raw, 'jobTitle', 'JobTitle'),
    email: pick(raw, 'email', 'Email'),
    invitationEvaluationStatus,
    status: pick(
      raw,
      'invitationEvaluationStatusLabel',
      'InvitationEvaluationStatusLabel'
    ),
    keterangan: pick(raw, 'attendanceType', 'AttendanceType'),
  };
};

export const unwrapAttendanceConfirmationList = result => {
  if (!result) return [];

  if (Array.isArray(result)) {
    return result.map(normalizeAttendanceConfirmation).filter(Boolean);
  }

  const nested = result.data ?? result.Data ?? result;

  if (Array.isArray(nested)) {
    return nested.map(normalizeAttendanceConfirmation).filter(Boolean);
  }

  const items =
    nested?.items ??
    nested?.Items ??
    nested?.attendances ??
    nested?.Attendances ??
    [];

  return (Array.isArray(items) ? items : [])
    .map(normalizeAttendanceConfirmation)
    .filter(Boolean);
};

const VISIBLE_FREIGHT_COST_COLUMNS = CARGO_DELIVERY_COLUMNS.filter(
  column => !column.hidden
);

export const FREIGHT_COST_COLUMNS = VISIBLE_FREIGHT_COST_COLUMNS.map(
  ({ key, header }) => ({
    key,
    header,
  })
);

export const getFreightCostRows = deliveryParcelResponse =>
  mapDeliveryToRows(deliveryParcelResponse).map(row =>
    VISIBLE_FREIGHT_COST_COLUMNS.reduce(
      (acc, column) => {
        const value = column.accessor(row.source);
        acc[column.key] =
          value === null || value === undefined || value === '' ? '-' : value;
        return acc;
      },
      { id: row.id }
    )
  );
